/**
 * ITER299 — Shared design-system primitives (Restored Brand Identity).
 *
 * The ITER298 refactor established design tokens but flattened the visual
 * hierarchy. ITER299 restores the filled dark-green header pattern using
 * `var(--color-primary)` (#143E30), applied as a *compact* card-header —
 * NOT a full-width page banner.
 *
 * Every Ops + Marketing surface consumes these primitives so a future
 * brand tweak only requires editing this file + the CSS variables.
 *
 * Design rules
 *   • Filled headers use var(--color-primary) with white text + white icons.
 *   • Headers wrap the content container (max-w-7xl), never the raw viewport.
 *   • Padding: px-4 py-2.5 — compact, not banner-height.
 *   • KPI cards: dark-green top strip + white body with the metric.
 *   • Tables: existing dark-green thead is correct — unchanged.
 */
import React from 'react';

// JS mirror of the CSS variables (see /app/frontend/src/index.css).
export const TOKENS = {
  primary:        'var(--color-primary)',
  primaryHover:   'var(--color-primary-hover)',
  primaryTint:    'var(--color-primary-tint)',
  primaryTint2:   'var(--color-primary-tint-2)',
  onPrimary:      'var(--color-text-on-primary)',
  textPrimary:    'var(--color-text-primary)',
  textMuted:      'var(--color-text-muted)',
  border:         'var(--color-border)',
  borderSoft:     'var(--color-border-soft)',
  background:     'var(--color-background)',
  surfaceMuted:   'var(--color-surface-muted)',
};

// ────────────────────────────────────────────────────────────────
//  <PageTitle> — top of a page. Compact card-style filled header.
// ────────────────────────────────────────────────────────────────
export function PageTitle({ title, subtitle, icon: Icon, actions, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-md flex items-center justify-between gap-4 mb-4 shadow-sm"
      style={{ background: TOKENS.primary, padding: '10px 16px' }}
    >
      <div className="flex items-center gap-3 min-w-0">
        {Icon && <Icon className="w-4 h-4 shrink-0" style={{ color: TOKENS.onPrimary }} />}
        <div className="min-w-0">
          <h1 className="text-sm font-semibold uppercase tracking-[0.14em]"
              style={{ color: TOKENS.onPrimary }}>
            {title}
          </h1>
          {subtitle && (
            <p className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.78)' }}>
              {subtitle}
            </p>
          )}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
//  <SectionHeader> — section divider on a page. Same premium filled
//  card-header pattern as PageTitle — compact, not banner-height.
//  Kept as a distinct component from PageTitle so future tweaks
//  (e.g. slightly smaller title size) can diverge cleanly.
// ────────────────────────────────────────────────────────────────
export function SectionHeader({ title, subtitle, icon: Icon, actions, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-md flex items-center justify-between gap-3 mb-3 shadow-sm"
      style={{ background: TOKENS.primary, padding: '9px 14px' }}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        {Icon && <Icon className="w-4 h-4 shrink-0" style={{ color: TOKENS.onPrimary }} />}
        <div className="min-w-0">
          <h2 className="text-[12px] font-semibold uppercase tracking-[0.12em]"
              style={{ color: TOKENS.onPrimary }}>
            {title}
          </h2>
          {subtitle && (
            <p className="text-[11px] leading-snug" style={{ color: 'rgba(255,255,255,0.72)' }}>
              {subtitle}
            </p>
          )}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

// Backward-compat alias for pages that still import SectionBand.
export const SectionBand = SectionHeader;

// ────────────────────────────────────────────────────────────────
//  <SectionTitle> — LEVEL-2 header for mid-page sections.
//  Plain layout, NO filled background — icon + dark-green title +
//  thin divider. Used to visually separate sections *within* a page
//  without competing with the Level-1 page banner or the Level-3
//  KPI card headers.
//
//  Design rules
//    • Icon + title use `var(--color-primary)`.
//    • Thin (1px) divider directly below, `var(--color-border)`.
//    • Comfortable top margin so sections "breathe".
//    • Optional right-side `actions` slot for e.g. a count pill or link.
// ────────────────────────────────────────────────────────────────
export function SectionTitle({ title, subtitle, icon: Icon, actions, testId,
                                className = '' }) {
  return (
    <div data-testid={testId} className={`mt-6 mb-3 ${className}`}>
      <div className="flex items-end justify-between gap-3 pb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          {Icon && (
            <Icon
              className="w-4 h-4 shrink-0"
              style={{ color: TOKENS.primary }}
              strokeWidth={2.25}
            />
          )}
          <div className="min-w-0">
            <h2
              className="text-[13px] font-semibold tracking-tight leading-none"
              style={{ color: TOKENS.primary }}
            >
              {title}
            </h2>
            {subtitle && (
              <p
                className="text-[11px] mt-1 leading-snug"
                style={{ color: TOKENS.textMuted }}
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {actions && (
          <div className="flex items-center gap-2 shrink-0 text-[11px]"
               style={{ color: TOKENS.textMuted }}>
            {actions}
          </div>
        )}
      </div>
      <div className="h-px w-full" style={{ background: TOKENS.border }} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
//  <KpiCard> — filled dark-green top strip (label + optional RAG),
//  white body with the metric value.
// ────────────────────────────────────────────────────────────────
const RAG_COLOURS = {
  good:    '#10B981',
  warning: '#F59E0B',
  poor:    '#DC2626',
  neutral: '#9CA3AF',
};

export function KpiCard({ label, value, sublabel, rag, delta, icon: Icon, testId }) {
  const dot = rag ? RAG_COLOURS[rag] || RAG_COLOURS.neutral : null;
  return (
    <div
      data-testid={testId}
      className="rounded-md border overflow-hidden bg-white shadow-sm"
      style={{ borderColor: TOKENS.border }}
    >
      <div
        className="flex items-center justify-between px-3 py-1.5"
        style={{ background: TOKENS.primary }}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          {Icon && <Icon className="w-3 h-3 shrink-0" style={{ color: TOKENS.onPrimary }} />}
          <div className="text-[10px] uppercase tracking-[0.16em] font-semibold truncate"
               style={{ color: TOKENS.onPrimary }}>
            {label}
          </div>
        </div>
        {dot && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: dot }} />}
      </div>
      <div className="px-3 py-2.5 bg-white">
        <div className="text-2xl font-semibold leading-tight"
             style={{ color: TOKENS.textPrimary }}>
          {value}
        </div>
        {sublabel && (
          <div className="text-[11px] mt-0.5" style={{ color: TOKENS.textMuted }}>
            {sublabel}
          </div>
        )}
        {delta != null && (
          <div className={`text-[11px] font-medium mt-1 ${delta >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
            {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
//  <TableHeader> — existing dark-green thead pattern.
//  White uppercase column labels on primary background.
// ────────────────────────────────────────────────────────────────
export function TableHeader({ columns, className = '' }) {
  return (
    <thead className={className} style={{ background: TOKENS.primary }}>
      <tr className="text-left text-[10px] uppercase tracking-[0.14em] font-semibold"
          style={{ color: TOKENS.onPrimary }}>
        {columns.map((c, i) => (
          <th key={i} className={`px-3 py-2 ${c.className || ''}`} style={c.style}>
            {c.label}
          </th>
        ))}
      </tr>
    </thead>
  );
}

// ────────────────────────────────────────────────────────────────
//  <CardHeader> — filled dark-green header at the TOP of a card.
//  Use when a card has a distinct title bar rather than an inline
//  section header. Body of the card sits below this on a white bg.
// ────────────────────────────────────────────────────────────────
export function CardHeader({ title, icon: Icon, actions, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex items-center justify-between px-4 py-2"
      style={{ background: TOKENS.primary }}
    >
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon className="w-4 h-4 shrink-0" style={{ color: TOKENS.onPrimary }} />}
        <h3 className="text-[12px] font-semibold uppercase tracking-[0.12em]"
            style={{ color: TOKENS.onPrimary }}>
          {title}
        </h3>
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
//  <PrimaryButton> — primary CTA. Solid primary background.
// ────────────────────────────────────────────────────────────────
export function PrimaryButton({ children, onClick, disabled, testId,
                                 className = '', type = 'button', size = 'md' }) {
  const pad = size === 'sm' ? 'px-3 py-1.5 text-xs'
            : size === 'lg' ? 'px-5 py-2.5 text-sm'
            : 'px-4 py-2 text-sm';
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-md font-medium transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed shadow-sm ${pad} ${className}`}
      style={{
        background: disabled ? TOKENS.primaryTint : TOKENS.primary,
        color: TOKENS.onPrimary,
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = TOKENS.primaryHover; }}
      onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.background = TOKENS.primary; }}
    >
      {children}
    </button>
  );
}
